import React from 'react';
import { cn } from '../../../components/ui/_cn';
import TWInput from '../../../components/ui/tw-input';
import ToggleSwitch from '../../../components/ui/toggle-switch';
import StatusBadge from '../../../components/ui/status-badge';
import DishIcon from '../../../components/ui/dish-icon';
import { productTone } from '../../../utils/productTone';

// One source of truth for the grid: header and every row use the same template.
// img 44px | product 1fr | main option 150px | status 120px | low-stock 104px | tracking 80px | qty 104px
const GRID_COLUMNS = '44px minmax(0,1fr) 150px 120px 104px 80px 104px';

// Visibility is a separate fact from stock (follow-up #20): shown as a
// neutral tag, never folded into the stock pill.
export const hiddenTagFor = (row) => (row.isHidden ? 'Hidden' : null);

// Resolve the stock-status badge for a row from its live tracking toggle + server flags.
export const statusBadgeFor = (row, tracking) => {
  if (!tracking) return { tone: 'slate', label: 'Not tracked', dot: false };
  if (row.isOutOfStock) return { tone: 'rose', label: 'Out of stock', dot: true };
  if (row.isLowInventory) return { tone: 'amber', label: 'Low stock', dot: true };
  return { tone: 'emerald', label: 'In stock', dot: true };
};

// Small rounded product thumbnail. With no photo we fall back to the same tonal
// dish tile the kiosk uses: a quiet pastel fill keyed off the product name, with
// a dish glyph tinted one shade deeper. imagePath arrives from thisway-api once
// WS2a ships; read it defensively.
const Thumb = ({ row }) => {
  if (row.imagePath) {
    return (
      <img
        src={row.imagePath}
        alt={row.productName}
        className="h-9 w-9 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
      />
    );
  }
  const tone = productTone(row.productName || '');
  return (
    <div
      data-testid={`inventory-thumb-placeholder-${row.itemId}`}
      className="flex h-9 w-9 items-center justify-center rounded-md ring-1 ring-slate-200 dark:ring-slate-700"
      style={{ backgroundColor: tone.bg, color: tone.ink }}
      aria-hidden
    >
      <DishIcon className="h-2/5 w-2/5" />
    </div>
  );
};

const cellPad = 'px-3 py-2.5';

const InventoryTable = ({ categories, draft, errors, onChangeRow, canEdit, dirtyIds }) => (
  <div className="max-h-full overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
    <div className="min-w-[760px]">
      {/* Single sticky header — sits just under the 8vh app top bar.
          z-[5] keeps it above the scrolling rows but below the global top bar
          (z-10), so the company-switcher dropdown can overlap it. */}
      <div
        data-testid="inventory-grid-header"
        className="sticky top-0 z-[5] grid items-center rounded-t-lg border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        style={{ gridTemplateColumns: GRID_COLUMNS }}
      >
        <div className={cellPad} aria-hidden />
        <div className={cellPad}>Product</div>
        <div className={cellPad}>Main Option</div>
        <div className={cellPad}>Status</div>
        <div className={cellPad}>Low Stock Alert</div>
        <div className={cellPad}>Stock Tracking</div>
        <div className={cellPad}>Quantity</div>
      </div>

      {categories.map((cat) => (
        <React.Fragment key={cat.categoryId}>
          {/* Full-width category separator spanning all columns. */}
          <div
            data-testid={`inventory-category-${cat.categoryId}`}
            className="flex items-center gap-2.5 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
          >
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {cat.categoryName}
            </h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-slate-700 dark:text-slate-300">
              {(cat.items || []).length}
            </span>
          </div>

          {(cat.items || []).map((row) => {
            const tracking = draft[row.itemId]?.trackingEnabled;
            const badge = statusBadgeFor(row, tracking);
            return (
              <div
                key={row.itemId}
                data-testid={`inventory-row-${row.itemId}`}
                className={cn(
                  'grid items-center border-b border-slate-100 transition-colors last:border-b-0 last:rounded-b-lg dark:border-slate-800',
                  dirtyIds?.has(row.itemId)
                    ? 'bg-sky-50/70 dark:bg-sky-900/20'
                    : 'bg-white dark:bg-slate-900',
                )}
                style={{ gridTemplateColumns: GRID_COLUMNS }}
              >
                <div className="flex items-center justify-center py-2 pl-3">
                  <Thumb row={row} />
                </div>
                <div className={cn(cellPad, 'font-medium text-slate-800 dark:text-slate-100')}>
                  {row.productName}
                  {hiddenTagFor(row) && (
                    <span className="ml-1 inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Hidden
                    </span>
                  )}
                </div>
                <div className={cn(cellPad, 'text-slate-500 dark:text-slate-400')}>
                  {row.optionName}
                </div>
                <div className={cellPad}>
                  <StatusBadge tone={badge.tone} label={badge.label} size="sm" dot={badge.dot} />
                </div>
                <div className={cellPad}>
                  <TWInput
                    type="number"
                    min={0}
                    size="sm"
                    data-testid={`inventory-threshold-${row.itemId}`}
                    disabled={!canEdit || !tracking}
                    placeholder="—"
                    value={draft[row.itemId]?.lowStockThreshold ?? ''}
                    onChange={(e) => onChangeRow(row.itemId, 'lowStockThreshold', e.target.value)}
                    error={errors[row.itemId]?.lowStockThreshold}
                    wrapperClassName="w-20"
                  />
                </div>
                <div className={cellPad}>
                  <ToggleSwitch
                    checked={tracking}
                    disabled={!canEdit}
                    tone="emerald"
                    onChange={(val) => onChangeRow(row.itemId, 'trackingEnabled', val)}
                  />
                </div>
                <div className={cellPad}>
                  <TWInput
                    type="number"
                    min={0}
                    size="sm"
                    data-testid={`inventory-qty-${row.itemId}`}
                    disabled={!canEdit || !tracking}
                    placeholder={tracking ? '0' : '—'}
                    value={draft[row.itemId]?.quantity ?? ''}
                    onChange={(e) => onChangeRow(row.itemId, 'quantity', e.target.value)}
                    error={errors[row.itemId]?.quantity}
                    wrapperClassName="w-20"
                  />
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  </div>
);

export default InventoryTable;
